import { useMemo, useState, useEffect, useRef, type ReactNode } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { MagnifyingGlassIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { Input } from "@components/ui/input";
import { cn } from "~/lib/utils";

export type PageLike = { id: number; route?: string | null; title?: string | null };

/** A choice pinned above the page list — e.g. "Create a new page (Recommended)". */
export type PagePickerLeadingOption = { value: string; label: ReactNode; sublabel?: string };

/**
 * Controlled, side-effect-free searchable page dropdown — the canonical,
 * reusable picker. `PageSwitcher` wraps this and binds it to the preview store
 * (selecting a page drives the iframe); other surfaces (the lead-magnet form)
 * bind it to their own local state and pass a `leadingOption` (e.g. "Create a
 * new page").
 *
 * Styles mirror `SiteNameDropdown` (the domain picker) so the builder's pickers
 * read as one family.
 */
export function PagePicker({
  pages,
  value,
  onChange,
  leadingOption,
  placeholder = "Select a page",
  className,
  testIdPrefix = "page-picker",
}: {
  pages: PageLike[];
  value: string | null;
  onChange: (value: string) => void;
  leadingOption?: PagePickerLeadingOption;
  placeholder?: string;
  className?: string;
  testIdPrefix?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const routedPages = useMemo(
    () => pages.filter((p): p is PageLike & { route: string } => !!p.route),
    [pages],
  );
  const activePage = routedPages.find((p) => p.route === value);

  const filteredPages = useMemo(() => {
    if (!query.trim()) return routedPages;
    const needle = query.trim().toLowerCase();
    return routedPages.filter((page) =>
      `${page.route ?? ""} ${page.title ?? ""}`.toLowerCase().includes(needle),
    );
  }, [routedPages, query]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setQuery("");
  };

  useEffect(() => {
    if (isOpen) requestAnimationFrame(() => inputRef.current?.focus());
  }, [isOpen]);

  const triggerLabel: ReactNode =
    leadingOption && value === leadingOption.value ? (
      leadingOption.label
    ) : activePage ? (
      <>
        <span className="font-mono">{activePage.route}</span>
        {activePage.title && activePage.title !== activePage.route && (
          <span className="text-base-400"> · {activePage.title}</span>
        )}
      </>
    ) : (
      <span className="text-base-400">{placeholder}</span>
    );

  const select = (next: string) => {
    onChange(next);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-trigger`}
          className={cn(
            "flex w-64 shrink-0 items-center justify-between gap-2",
            "rounded-lg border border-neutral-300 bg-white px-3 py-2.5",
            "text-sm text-left transition-colors",
            "hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
            className,
          )}
        >
          <span className="truncate text-base-600">{triggerLabel}</span>
          <ChevronDownIcon className="size-4 text-base-400 shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="max-h-96 overflow-y-auto">
          <div className="px-4 py-3 border-b border-neutral-100">
            <div className="flex items-center rounded-lg border border-neutral-200 bg-neutral-50 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
              <MagnifyingGlassIcon className="size-4 text-base-400 ml-3 shrink-0" />
              <Input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pages…"
                className="text-sm bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
          </div>

          <div className="px-4 py-3">
            <div className="flex flex-col gap-0.5" data-testid={`${testIdPrefix}-list`}>
              {leadingOption && (
                <button
                  type="button"
                  onClick={() => select(leadingOption.value)}
                  data-testid={`${testIdPrefix}-option-${leadingOption.value}`}
                  data-active={value === leadingOption.value || undefined}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-colors",
                    value === leadingOption.value ? "bg-primary-50" : "hover:bg-neutral-50",
                  )}
                >
                  <span className="flex-1 min-w-0">
                    <span className="text-primary-700 font-medium">{leadingOption.label}</span>
                    {leadingOption.sublabel && (
                      <span className="block text-xs text-base-400">{leadingOption.sublabel}</span>
                    )}
                  </span>
                  {value === leadingOption.value && (
                    <CheckIcon className="size-4 text-primary-600 shrink-0" />
                  )}
                </button>
              )}

              {filteredPages.length === 0 && !leadingOption ? (
                <div className="px-3 py-2 text-sm text-base-400">No matches</div>
              ) : (
                filteredPages.map((page) => {
                  const isActive = page.route === value;
                  return (
                    <button
                      key={page.id}
                      type="button"
                      onClick={() => select(page.route)}
                      data-testid={`${testIdPrefix}-option-${page.route}`}
                      data-active={isActive || undefined}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left transition-colors",
                        isActive ? "bg-neutral-100" : "hover:bg-neutral-50",
                      )}
                    >
                      <span className="flex-1 min-w-0 flex items-baseline gap-2 truncate">
                        <span className="font-mono text-base-600 shrink-0">{page.route}</span>
                        {page.title && page.title !== page.route && (
                          <span className="truncate text-base-400">{page.title}</span>
                        )}
                      </span>
                      {isActive && <CheckIcon className="size-4 text-base-500 shrink-0" />}
                    </button>
                  );
                })
              )}
              {filteredPages.length === 0 && leadingOption && query.trim() && (
                <div className="px-3 py-2 text-sm text-base-400">No matching pages</div>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
